import { BaseService } from './BaseService';
import { API_ENDPOINTS } from './config';
import {
  BusinessModel,
  BusinessModelCreate,
  BusinessModelUpdate,
  BusinessModelComponent,
  BusinessModelComponentCreate,
  BusinessModelComponentUpdate,
  BusinessModelType,
} from '../../types/businessModel';

export class BusinessModelService extends BaseService {
  // Request deduplication: track in-flight requests
  private static pendingGetByTreeId: Map<string, Promise<BusinessModel[]>> = new Map();
  private static pendingGetActiveByTreeId: Map<string, Promise<BusinessModel | null>> = new Map();

  static async getAll(treeId: string): Promise<BusinessModel[]> {
    return this.get<BusinessModel[]>(`/business-models/tree/${treeId}`);
  }

  static async getById(id: string): Promise<BusinessModel> {
    return this.get<BusinessModel>(`/business-models/${id}`);
  }

  static async create(model: BusinessModelCreate): Promise<BusinessModel> {
    return this.post<BusinessModel>(
      '/business-models',
      model as unknown as Record<string, unknown>
    );
  }

  static async update(id: string, model: BusinessModelUpdate): Promise<BusinessModel> {
    return this.put<BusinessModel>(
      `/business-models/${id}`,
      model as unknown as Record<string, unknown>
    );
  }

  static async delete(id: string): Promise<void> {
    return this.delete(`/business-models/${id}`);
  }

  static async getByTreeId(treeId: string): Promise<BusinessModel[]> {
    // If there's already a pending request for this treeId, return it
    if (this.pendingGetByTreeId.has(treeId)) {
      return this.pendingGetByTreeId.get(treeId)!;
    }

    // Create a new request
    const requestPromise = this.get<BusinessModel[]>(`/business-models/tree/${treeId}`)
      .finally(() => {
        // Clean up after request completes
        this.pendingGetByTreeId.delete(treeId);
      });

    this.pendingGetByTreeId.set(treeId, requestPromise);
    return requestPromise;
  }

  static async getActiveByTreeId(treeId: string): Promise<BusinessModel | null> {
    // If there's already a pending request for this treeId, return it
    if (this.pendingGetActiveByTreeId.has(treeId)) {
      return this.pendingGetActiveByTreeId.get(treeId)!;
    }

    // Create a new request
    const requestPromise = (async (): Promise<BusinessModel | null> => {
      try {
        // Primary: Try active endpoint
        const response = await this.get<BusinessModel>(`/business-models/tree/${treeId}/active`);
        return response;
      } catch (error: any) {
        // Handle 404 as expected case (no active model found)
        if (error.response?.status === 404) {
          return null;
        }
        
        // Check if it's a retryable error (500 server error, not CORS)
        const isCorsError = error.code === 'ERR_NETWORK' || 
                           error.message?.includes('CORS') ||
                           error.message?.includes('Access-Control-Allow-Origin');
        const isRetryable = error.response?.status === 500 && !isCorsError;
        
        if (isRetryable) {
          // Retry once with delay
          try {
            await new Promise(resolve => setTimeout(resolve, 1000));
            const response = await this.get<BusinessModel>(`/business-models/tree/${treeId}/active`);
            return response;
          } catch (retryError) {
            // Retry failed, use fallback
            console.warn('[BusinessModelService] Active endpoint retry failed, using fallback');
          }
        }
        
        // Fallback: Get all models and filter client-side
        // This works around 500 errors and CORS issues
        if (isCorsError || error.response?.status >= 500) {
          try {
            console.warn('[BusinessModelService] Using fallback: fetching all models and filtering client-side');
            const allModels = await this.getByTreeId(treeId);
            
            // Filter for active models - handle different status field names
            const activeModels = allModels.filter((model: BusinessModel) => {
              // Check various possible status fields
              const status = (model as any).status;
              const isActive = (model as any).is_active;
              
              return status === 'active' || 
                     status === true || 
                     status === null || 
                     status === undefined ||
                     isActive === true ||
                     isActive === null ||
                     isActive === undefined;
            });
            
            // Return first active model, or null if none found
            return activeModels.length > 0 ? activeModels[0] : null;
          } catch (fallbackError: any) {
            // All endpoints failed - log and return null to prevent UI breakage
            console.error('[BusinessModelService] All business model endpoints failed:', {
              treeId,
              originalError: error.response?.status || error.code,
              fallbackError: fallbackError.response?.status || fallbackError.code,
            });
            return null;
          }
        }
        
        // Re-throw other errors (like 400, 401, 403)
        throw error;
      }
    })().finally(() => {
      // Clean up after request completes
      this.pendingGetActiveByTreeId.delete(treeId);
    });

    this.pendingGetActiveByTreeId.set(treeId, requestPromise);
    return requestPromise;
  }

  /**
   * Get all components for a business model
   */
  static async getComponents(modelId: string): Promise<BusinessModelComponent[]> {
    try {
      const response = await this.get<BusinessModelComponent[] | { components: BusinessModelComponent[] }>(
        API_ENDPOINTS.BUSINESS_MODELS.COMPONENTS(modelId)
      );
      
      // Handle different response formats
      if (Array.isArray(response)) {
        return response;
      }
      if (response && typeof response === 'object' && 'components' in response) {
        return (response as { components: BusinessModelComponent[] }).components;
      }
      return [];
    } catch (error) {
      console.error('[BusinessModelService] Error fetching components:', error);
      return [];
    }
  }

  /**
   * Add a component to a business model
   */
  static async addComponent(
    modelId: string,
    componentData: BusinessModelComponentCreate
  ): Promise<BusinessModelComponent> {
    return this.post<BusinessModelComponent>(
      API_ENDPOINTS.BUSINESS_MODELS.COMPONENTS(modelId),
      componentData as unknown as Record<string, unknown>
    );
  }

  /**
   * Update a component
   */
  static async updateComponent(
    modelId: string,
    componentId: string,
    updateData: BusinessModelComponentUpdate
  ): Promise<BusinessModelComponent> {
    return this.put<BusinessModelComponent>(
      API_ENDPOINTS.BUSINESS_MODELS.COMPONENT_BY_ID(modelId, componentId),
      updateData as unknown as Record<string, unknown>
    );
  }

  /**
   * Remove a component from a business model
   */
  static async removeComponent(modelId: string, componentId: string): Promise<void> {
    return this.delete(API_ENDPOINTS.BUSINESS_MODELS.COMPONENT_BY_ID(modelId, componentId));
  }

  /**
   * Get business model types
   */
  static async getModelTypes(): Promise<BusinessModelType[]> {
    try {
      const response = await this.get<BusinessModelType[] | { types: BusinessModelType[] }>(
        API_ENDPOINTS.BUSINESS_MODELS.TYPES
      );
      
      // Handle different response formats
      if (Array.isArray(response)) {
        return response;
      }
      if (response && typeof response === 'object' && 'types' in response) {
        return (response as { types: BusinessModelType[] }).types;
      }
      return [];
    } catch (error) {
      console.error('[BusinessModelService] Error fetching model types:', error);
      return [];
    }
  }
}
